// src/pages/Product/AddProduct.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import categoryService from '../../services/category.service';
import productService from '../../services/product.service';
import '../../styles/AddPages.css';


const AddProduct = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    userId: '',
    userName: '',
    role: '',
    companyId: '',
    companyName: '',
    categoryId: '',
    productName: '',
    productAndPacking: '',
    ratePerUnit: '',
    itemPerBox: '',
    ratePerBox: '',
    status: 'Available',
    briefDescription: '',
    photo: null,
  });

  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (user) {
      const userId = user.employeeCode || user.id;
      const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
      const role = user.role?.name || user.role;
      
      let companyId = '';
      let companyName = 'No company selected';
      
      if (isSuperAdmin) {
        companyId = localStorage.getItem('selectedCompanyId') || '';
        const companyButton = document.querySelector('button[class*="company"]');
        if (companyButton) {
          companyName = companyButton.textContent?.trim() || 'No company selected';
        } else if (companyId) {
          companyName = `Company ID: ${companyId}`;
        }
      } else {
        companyId = user.companyId || '';
        companyName = user.company?.name || user.companyName || 'No company assigned';
      }
      
      setFormData(prev => ({ ...prev, userId, userName, role, companyId, companyName }));
      
      if (companyId) {
        fetchCategories(companyId);
      }
    }
  }, [user, isSuperAdmin]);

  const fetchCategories = async (companyId) => {
    try {
      const response = await categoryService.getAllCategories(companyId);
      if (response.success && response.data) {
        const categoriesData = response.data.categories || response.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.companyId) {
      newErrors.companyId = isSuperAdmin 
        ? 'Please select a company from the navbar first'
        : 'You are not assigned to any company';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    if (!formData.productName.trim()) {
      newErrors.productName = 'Product name is required';
    }

    if (!formData.productAndPacking.trim()) {
      newErrors.productAndPacking = 'Product & packing is required';
    }

    if (!formData.ratePerUnit || parseFloat(formData.ratePerUnit) <= 0) {
      newErrors.ratePerUnit = 'Valid rate per unit is required';
    }

    if (!formData.itemPerBox || parseInt(formData.itemPerBox) <= 0) {
      newErrors.itemPerBox = 'Valid items per box is required';
    }

    if (!formData.ratePerBox || parseFloat(formData.ratePerBox) <= 0) {
      newErrors.ratePerBox = 'Valid rate per box is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const dataToSend = {
        productName: formData.productName.trim(),
        productAndPacking: formData.productAndPacking.trim(),
        ratePerUnit: parseFloat(formData.ratePerUnit),
        itemPerBox: parseInt(formData.itemPerBox),
        ratePerBox: parseFloat(formData.ratePerBox),
        status: formData.status,
        briefDescription: formData.briefDescription?.trim() || '',
        categoryId: parseInt(formData.categoryId),
        companyId: parseInt(formData.companyId),
      };
      
      if (photoPreview) {
        dataToSend.photo = photoPreview;
      }
      
      const response = await productService.createProduct(dataToSend);
      
      if (response.success) {
        alert('Product added successfully!');
        navigate('/product/view');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add product');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-product-page">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard/overview">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/product/view">Products</a>
          </li>
          <li className="breadcrumb-item active">Add Product</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="page-header">
        <h4 className="page-title">Add New Product</h4>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* User Information */}
          <div className="form-section">
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">User ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.userId}
                  readOnly
                  disabled
                  style={{ backgroundColor: '#f7fafc' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">User Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.userName}
                  readOnly
                  disabled
                  style={{ backgroundColor: '#f7fafc' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Role</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.role}
                  readOnly
                  disabled
                  style={{ backgroundColor: '#f7fafc' }}
                />
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div className="form-section">
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Company <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`form-input ${errors.companyId ? 'error' : ''}`}
                  value={formData.companyName}
                  readOnly
                  disabled
                  style={{ 
                    backgroundColor: formData.companyId ? '#e6fffa' : '#fff5f5',
                    color: formData.companyId ? '#234e52' : '#c53030',
                    fontWeight: '500'
                  }}
                />
                {errors.companyId && (
                  <span className="error-message">{errors.companyId}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Category <span className="required">*</span>
                </label>
                <select
                  name="categoryId"
                  className={`form-input ${errors.categoryId ? 'error' : ''}`}
                  value={formData.categoryId}
                  onChange={handleChange}
                  disabled={!formData.companyId}
                >
                  <option value="">Select Category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.categoryId && (
                  <span className="error-message">{errors.categoryId}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Product Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="productName"
                  className={`form-input ${errors.productName ? 'error' : ''}`}
                  value={formData.productName}
                  onChange={handleChange}
                  placeholder="Enter product name"
                />
                {errors.productName && (
                  <span className="error-message">{errors.productName}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Product & Packing <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="productAndPacking"
                  className={`form-input ${errors.productAndPacking ? 'error' : ''}`}
                  value={formData.productAndPacking}
                  onChange={handleChange}
                  placeholder="e.g., 500ml bottle"
                />
                {errors.productAndPacking && (
                  <span className="error-message">{errors.productAndPacking}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Rate per Unit <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="ratePerUnit"
                  className={`form-input ${errors.ratePerUnit ? 'error' : ''}`}
                  value={formData.ratePerUnit}
                  onChange={handleChange}
                  placeholder="Enter rate"
                  step="0.01"
                  min="0"
                />
                {errors.ratePerUnit && (
                  <span className="error-message">{errors.ratePerUnit}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Item per Box <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="itemPerBox"
                  className={`form-input ${errors.itemPerBox ? 'error' : ''}`}
                  value={formData.itemPerBox}
                  onChange={handleChange}
                  placeholder="Enter quantity"
                  min="1"
                />
                {errors.itemPerBox && (
                  <span className="error-message">{errors.itemPerBox}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Rate per Box <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="ratePerBox"
                  className={`form-input ${errors.ratePerBox ? 'error' : ''}`}
                  value={formData.ratePerBox}
                  onChange={handleChange}
                  placeholder="Enter rate"
                  step="0.01"
                  min="0"
                />
                {errors.ratePerBox && (
                  <span className="error-message">{errors.ratePerBox}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  className="form-input"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="Available">Available</option>
                  <option value="Out of Stock">Out of Stock</option>
                  <option value="Discontinued">Discontinued</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Brief Description</label>
                <textarea
                  name="briefDescription"
                  className="form-textarea"
                  value={formData.briefDescription}
                  onChange={handleChange}
                  placeholder="Enter product description"
                  rows="3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Product Photo</label>
                <input
                  type="file"
                  name="photo"
                  className="form-input"
                  onChange={handleChange}
                  accept="image/*"
                />
                {photoPreview && (
                  <div className="photo-preview-large">
                    <img src={photoPreview} alt="Preview" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/product/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !formData.companyId}
            >
              {isSubmitting ? 'Adding Product...' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;